import { useState } from 'react'
import type { IdsFile } from './lib/idsParse'
import { getModelSuggestions } from './lib/idsSuggest'
import { type Auditoria, type Gravidade, NOME_DA_GRAVIDADE, auditarIds } from './lib/idsAuditoria'

/** Auditar o IDS contra o modelo antes de validar: o que não pode dar
 *  certo, por especificação, e porquê. Ver `lib/idsAuditoria.ts`. */
export default function IdsAuditoria({
  ids,
  modelId,
  esquema,
}: {
  ids: IdsFile
  modelId: string
  esquema: string | null
}) {
  const [estado, setEstado] = useState<{ chave: string; auditoria: Auditoria } | null>(null)
  const [ocupado, setOcupado] = useState(false)
  const chave = `${modelId}|${ids.specs.length}|${ids.info.title ?? ''}`
  const auditoria = estado?.chave === chave ? estado.auditoria : null

  const correr = async () => {
    setOcupado(true)
    try {
      const modelo = await getModelSuggestions(modelId)
      setEstado({ chave, auditoria: auditarIds(ids, modelo, esquema) })
    } finally {
      setOcupado(false)
    }
  }

  const cor: Record<Gravidade, string> = {
    'aplica-a-nada': 'text-[var(--color-error-text)]',
    'falha-sempre': 'text-[var(--color-warn-text)]',
    aviso: 'text-[var(--color-text-muted)]',
  }

  return (
    <div className="flex flex-col gap-1.5 text-xs">
      {!auditoria ? (
        <button
          type="button"
          onClick={() => void correr()}
          disabled={ocupado}
          title="Lê o IDS e o que o modelo contém e diz, antes de validar, que especificações não se podem aplicar a nada ou vão falhar em todos os elementos, e porquê."
          className="self-start rounded border border-[var(--color-border)] px-2 py-1 hover:text-[var(--color-text)] disabled:opacity-50"
        >
          {ocupado ? 'A auditar…' : 'Auditar o IDS contra o modelo'}
        </button>
      ) : (
        <div className="rounded border border-[var(--color-border)] bg-[var(--color-bg)]/40 p-2">
          <p className="text-[var(--color-text-muted)]">
            {auditoria.specs.length} especificações: {auditoria.aplicamANada} não se aplicam a nada,{' '}
            {auditoria.falhamSempre} vão falhar em todos os elementos, {auditoria.comAvisos} a confirmar,{' '}
            {auditoria.limpas} sem reparos.
          </p>
          <ul className="mt-1.5 flex max-h-48 flex-col gap-1 overflow-y-auto">
            {auditoria.specs
              .filter((s) => s.achados.length > 0)
              .map((s) => (
                <li key={s.indice}>
                  <div className="font-medium text-[var(--color-text)]">{s.nome}</div>
                  <ul className="ml-2">
                    {s.achados.map((a, i) => (
                      <li key={i} className={cor[a.gravidade]}>
                        <span className="text-[10px] uppercase tracking-wide">{NOME_DA_GRAVIDADE[a.gravidade]}</span>
                        {' · '}
                        <span className="text-[10px]">{a.onde}</span>: {a.mensagem}
                      </li>
                    ))}
                  </ul>
                </li>
              ))}
          </ul>
          {auditoria.aplicamANada + auditoria.falhamSempre + auditoria.comAvisos === 0 && (
            <p className="mt-1 text-[var(--color-text-muted)]">Nada a apontar: tudo o que o IDS pede existe no modelo.</p>
          )}
        </div>
      )}
    </div>
  )
}
